import React, { useCallback, useEffect, useRef, useState } from "react";
import ConfirmationModal from "@enerex/unified-ui-react/ConfirmationModal";
import Check from "@enerex/unified-ui-react/form/Check";
import FormInput from "@enerex/unified-ui-react/form/FormInput";
import FormSelect from "@enerex/unified-ui-react/form/Select";
import Select from "components/UnifiedSelect";
import { useForm, useWatch } from "react-hook-form";
import * as api from "../utilityApi";
import { CommodityIcon } from "../CommodityIcons";
import { getIdentifier, getStateDisplayName, getStateName, getStateOptionValue, toOption, utilitySelectStyles } from "../utilityUtils";
import "./EditStateOperationModal.css";

const StateOperationModal = ({
  show,
  onHide,
  onSave,
  countries = [],
  states = [],
  existingStates = [],
  onCountryChange,
  saving,
}) => {
  const lastStateOperationSelection = useRef({ CountryIdentifier: "", StateId: "", StateName: "" });
  const [form, setForm] = useState({
    ...lastStateOperationSelection.current,
    CommodityIds: [],
  });
  const [hasPreviousSelection, setHasPreviousSelection] = useState(Boolean(lastStateOperationSelection.current.StateId) || existingStates.length > 0);
  const [modalCountries, setModalCountries] = useState([]);
  const [modalStates, setModalStates] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [uomOptions, setUomOptions] = useState([]);
  const [loadingUoms, setLoadingUoms] = useState(false);
  const [uomLoadError, setUomLoadError] = useState("");
  const initializedRef = useRef(false);
  const statesRef = useRef(states);
  const { control, getValues, reset, setValue } = useForm({
    defaultValues: {
      ServiceReferenceNumberRequired: false,
      ServiceReferenceNumberDescription: "",
      KeyNameRequired: false,
      KeyNameDescription: "",
      EnrollmentLeadTime: "",
      EnerexResidentialUOM: null,
      EnerexCommercialUOM: null,
      EnerexLargeCommercialUOM: null,
      CommodityElectric: false,
      CommodityGas: false,
    },
  });
  const nameKeyRequired = useWatch({ control, name: "KeyNameRequired" });
  const serviceReferenceRequired = useWatch({ control, name: "ServiceReferenceNumberRequired" });
  const commodityElectric = useWatch({ control, name: "CommodityElectric" });
  const commodityGas = useWatch({ control, name: "CommodityGas" });
  const previousCommodityRef = useRef({ electric: commodityElectric, gas: commodityGas });
  useEffect(() => {
    if (commodityElectric && commodityGas) {
      if (!previousCommodityRef.current.electric && commodityElectric) setValue("CommodityGas", false);
      else setValue("CommodityElectric", false);
    }
    previousCommodityRef.current = { electric: commodityElectric, gas: commodityGas };
  }, [commodityElectric, commodityGas, setValue]);

  useEffect(() => {
    statesRef.current = states;
  }, [states]);

  const loadStatesForCountry = useCallback(async (countryIdentifier) => {
    setLoadingStates(true);
    try {
      const nextStates = onCountryChange
        ? await onCountryChange(countryIdentifier)
        : countryIdentifier
          ? await api.getStatesByCountry(countryIdentifier)
          : await api.getStates();

      const nextModalStates = Array.isArray(nextStates) && nextStates.length ? nextStates : statesRef.current;
      setModalStates(Array.isArray(nextModalStates) ? nextModalStates : []);
    } catch (error) {
      console.error("Failed to load states for country", error);
      setModalStates([]);
    } finally {
      setLoadingStates(false);
    }
  }, [onCountryChange]);

  useEffect(() => {
    if (!show || countries.length) return;

    let active = true;
    api.getCountries()
      .then((nextCountries) => { if (active) setModalCountries(Array.isArray(nextCountries) ? nextCountries : []); })
      .catch((error) => {
        console.error("Failed to load countries", error);
        if (active) setModalCountries([]);
      });

    return () => { active = false; };
  }, [countries.length, show]);

  useEffect(() => {
    if (!show) return;

    const controller = new AbortController();
    setLoadingUoms(true);
    setUomLoadError("");
    api.getUnitMeasurements(controller.signal)
      .then((records) => {
        setUomOptions(records.map((record) => ({
          id: record.id ?? record.Id,
          name: record.name ?? record.Name,
        })).filter((record) => record.id && record.name));
      })
      .catch((error) => {
        if (error?.name === "AbortError") return;
        console.error("Failed to load units of measurement", error);
        setUomOptions([]);
        setUomLoadError("Unable to load units of measurement. Close the form and try again.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingUoms(false);
      });

    return () => controller.abort();
  }, [show]);

  const availableCountries = countries.length ? countries : modalCountries;

  const clearSelections = () => {
    setForm((current) => ({ ...current, CommodityIds: [] }));
    reset({
      ServiceReferenceNumberRequired: false,
      ServiceReferenceNumberDescription: "",
      KeyNameRequired: false,
      KeyNameDescription: "",
      EnrollmentLeadTime: "",
      EnerexResidentialUOM: null,
      EnerexCommercialUOM: null,
      EnerexLargeCommercialUOM: null,
      CommodityElectric: false,
      CommodityGas: false,
    });
  };

  useEffect(() => {
    if (!show) {
      initializedRef.current = false;
      return;
    }
    if (!initializedRef.current) {
      initializedRef.current = true;
      const existingState = existingStates[0];
      setHasPreviousSelection(Boolean(lastStateOperationSelection.current.StateId) || existingStates.length > 0);
      const defaultCountry =
        availableCountries.find((country) => (country.name || country.Name) === "USA") ||
        availableCountries[0];
      const defaultCountryIdentifier = getIdentifier(defaultCountry) || "";
      const preservedCountryIdentifier = form.CountryIdentifier || lastStateOperationSelection.current.CountryIdentifier || defaultCountryIdentifier;
      const preservedStateId = form.StateId || lastStateOperationSelection.current.StateId || existingState?.stateId || existingState?.StateId || "";
      const preservedStateName = form.StateName || lastStateOperationSelection.current.StateName || existingState?.stateName || existingState?.StateName || "";
      lastStateOperationSelection.current = {
        CountryIdentifier: preservedCountryIdentifier,
        StateId: preservedStateId,
        StateName: preservedStateName,
      };
      setForm((current) => ({
        ...current,
        CountryIdentifier: preservedCountryIdentifier,
        StateId: preservedStateId,
        StateName: preservedStateName,
        CommodityIds: [],
      }));
      reset({
        ServiceReferenceNumberRequired: false,
        ServiceReferenceNumberDescription: "",
        KeyNameRequired: false,
        KeyNameDescription: "",
        EnrollmentLeadTime: "",
        EnerexResidentialUOM: null,
        EnerexCommercialUOM: null,
        EnerexLargeCommercialUOM: null,
        CommodityElectric: false,
        CommodityGas: false,
      });
      loadStatesForCountry(preservedCountryIdentifier);
    }
  }, [availableCountries, existingStates, form.CountryIdentifier, loadStatesForCountry, reset, show]);

  const countryOptions = availableCountries.map((c) => toOption(c));
  const stateOptions = modalStates.map((s) => ({
    value: getStateOptionValue(s),
    label: getStateDisplayName(s) || getStateName(s),
    raw: s,
  }));
  const selectedCountry =
    countryOptions.find((option) => String(option.value) === String(form.CountryIdentifier)) || null;

  const handleCountryChange = async (value) => {
    const countryIdentifier = value?.value || "";
    lastStateOperationSelection.current = { ...lastStateOperationSelection.current, CountryIdentifier: countryIdentifier, StateId: "", StateName: "" };
    setForm((f) => ({ ...f, CountryIdentifier: countryIdentifier, StateId: "", StateName: "" }));
    await loadStatesForCountry(countryIdentifier);
  };

  return (
    <ConfirmationModal
      size="md"
      centered
      show={show}
      onHide={() => { clearSelections(); onHide(); }}
      title="Add State of Operations"
      confirmButtonLabel={saving ? "Adding..." : "Add State"}
      confirmButtonVariant="primary"
      isLoading={saving}
      onConfirm={() => {
        const values = getValues();
        const commodityIds = [values.CommodityElectric && 1, values.CommodityGas && 2].filter(Boolean);
        if (!form.StateId) return;
        const selectedRawState = modalStates.find((s) => String(getStateOptionValue(s)) === String(form.StateId));
        onSave({
          ...form,
          CommodityIds: commodityIds,
          SelectedState: selectedRawState || null,
          ServiceReferenceNumberRequired: Boolean(values.ServiceReferenceNumberRequired),
          ServiceReferenceNumberDescription: values.ServiceReferenceNumberDescription || null,
          KeyNameRequired: Boolean(values.KeyNameRequired),
          KeyNameDescription: values.KeyNameDescription ?? "",
          EnrollmentLeadTime: values.EnrollmentLeadTime ?? "",
          EnerexResidentialUOM: values.EnerexResidentialUOM?.id ?? null,
          EnerexCommercialUOM: values.EnerexCommercialUOM?.id ?? null,
          EnerexLargeCommercialUOM: values.EnerexLargeCommercialUOM?.id ?? null,
        });
      }}
    >
      <div className="utility-sop-add-fields">
        <div className="utility-sop-edit-column">
          <div className="utility-sop-field-group"><label htmlFor="utility-state-country">Country <span className="utility-sop-required">*</span></label><Select inputId="utility-state-country" options={countryOptions} value={selectedCountry} onChange={handleCountryChange} placeholder="Select country" styles={utilitySelectStyles} isDisabled={saving || hasPreviousSelection} noOptionsMessage={() => "No countries found"} menuPortalTarget={document.body} /></div>
          <div className="utility-sop-field-group"><label htmlFor="utility-state-state">State <span className="utility-sop-required">*</span></label><FormSelect inputId="utility-state-state" options={stateOptions} value={stateOptions.find((o) => String(o.value) === String(form.StateId)) || null} onChange={(value) => { const next = { StateId: value?.value || "", StateName: value?.label || "" }; lastStateOperationSelection.current = { ...lastStateOperationSelection.current, ...next }; setForm((f) => ({ ...f, ...next })); }} placeholder="Select state" isDisabled={saving || hasPreviousSelection} isLoading={loadingStates} noOptionsMessage={() => loadingStates ? "Loading states..." : "No states found"} isClearable getOptionValue={(option) => String(option.value)} getOptionLabel={(option) => option.label} menuPortalTarget={document.body} /></div>
          <Check id="utility-add-name-key-required" name="KeyNameRequired" control={control} label="Name Key Required" disabled={saving} />
          <FormInput type="text" name="KeyNameDescription" control={control} label="Name Key Description" disabled={saving || !nameKeyRequired} />
          <FormInput type="number" name="EnrollmentLeadTime" control={control} label="Enrollment Lead Time days" min={0} step={1} disabled={saving} />
          <FormInput type="select" name="EnerexCommercialUOM" control={control} label="Commercial UOM" options={uomOptions} placeholder="Select commercial UOM" isClearable isLoading={loadingUoms} disabled={saving} />
        </div>
        <div className="utility-sop-edit-column">
          <fieldset className="utility-sop-edit-commodities" aria-label="Commodities" disabled={saving}><div className="utility-sop-edit-commodity-options"><Check name="CommodityElectric" control={control} label={<CommodityIcon type="electric" />} disabled={saving} /><Check name="CommodityGas" control={control} label={<CommodityIcon type="gas" />} disabled={saving} /></div></fieldset>
          <div aria-hidden="true" />
          <Check id="utility-add-service-reference-required" name="ServiceReferenceNumberRequired" control={control} label="Service Reference Number Required" disabled={saving} />
          <FormInput type="text" name="ServiceReferenceNumberDescription" control={control} label="Service Reference Number Description" disabled={saving || !serviceReferenceRequired} />
          <FormInput type="select" name="EnerexResidentialUOM" control={control} label="Residential UOM" options={uomOptions} placeholder="Select residential UOM" isClearable isLoading={loadingUoms} disabled={saving} />
          <FormInput type="select" name="EnerexLargeCommercialUOM" control={control} label="Large commercial UOM" options={uomOptions} placeholder="Select large commercial UOM" isClearable isLoading={loadingUoms} disabled={saving} />
          {uomLoadError && <div role="alert">{uomLoadError}</div>}
        </div>
      </div>
    </ConfirmationModal>
  );
};

export default StateOperationModal;

